import { createFileRoute, Link } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { ErrorBanner } from '../components/ui/ErrorBanner';
import { PageHeader } from '../components/ui/PageHeader';
import { SkeletonList } from '../components/ui/Skeleton';
import {
  SERIENLEISTUNG_TURNUS_LABEL,
  SERIENLEISTUNG_TYP_LABEL,
  serienleistungenApi,
  type Serienleistung,
} from '../lib/serienleistungen-api';
import { requireAccess } from '../lib/guards';
import { formatGermanDateFromTimestamp } from '../lib/date-format';

export const Route = createFileRoute('/serienleistungen/')({
  beforeLoad: () => requireAccess('/api/v1/serienleistungen'),
  component: SerienleistungenIndexPage,
});

export function SerienleistungenIndexPage(): JSX.Element {
  const [aktivOnly, setAktivOnly] = useState(true);

  const serienQ = useQuery({
    queryKey: ['serienleistungen', 'list', aktivOnly],
    queryFn: () => serienleistungenApi.list({ aktivOnly }),
  });

  const items = serienQ.data?.items ?? [];

  return (
    <div className="space-y-6">
      <PageHeader
        title="Serienleistungen"
        description="Wiederkehrende Aufträge pro Objekt — Cron erzeugt täglich die fälligen Aufträge."
        actions={
          <Link
            to="/serienleistungen/neu"
            className="inline-flex items-center gap-2 rounded-lg bg-slate-900 px-4 py-2 text-sm font-medium text-white hover:bg-slate-800"
          >
            Neue Serie
          </Link>
        }
      />

      {serienQ.error ? <ErrorBanner error={serienQ.error as Error} /> : null}

      <label className="inline-flex items-center gap-2 text-sm text-slate-700">
        <input
          type="checkbox"
          checked={aktivOnly}
          onChange={(e) => setAktivOnly(e.target.checked)}
          className="h-4 w-4 rounded border-slate-300 text-brand-600 focus:ring-brand-500"
        />
        Nur aktive Serien anzeigen
      </label>

      {serienQ.isLoading ? (
        <SkeletonList />
      ) : items.length === 0 ? (
        <div className="rounded-lg border border-dashed border-slate-300 p-8 text-center text-sm text-slate-500">
          {aktivOnly
            ? 'Keine aktiven Serien — pausierte Serien einblenden via Checkbox oben.'
            : 'Noch keine Serien angelegt.'}
        </div>
      ) : (
        <ul className="space-y-3">
          {items.map((s) => (
            <SerienleistungCard key={s.id} serie={s} />
          ))}
        </ul>
      )}
    </div>
  );
}

function SerienleistungCard({ serie }: { serie: Serienleistung }): JSX.Element {
  return (
    <li>
      <Link
        to="/serienleistungen/$id"
        params={{ id: serie.id }}
        className="block rounded-lg border border-slate-200 bg-white p-4 hover:border-slate-300 hover:shadow-sm"
      >
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0 flex-1">
            <div className="truncate text-base font-medium text-slate-900">{serie.bezeichnung}</div>
            <div className="mt-1 text-xs text-slate-500">
              {SERIENLEISTUNG_TYP_LABEL[serie.leistungstyp]} ·{' '}
              {SERIENLEISTUNG_TURNUS_LABEL[serie.turnus]} · nächste Fälligkeit{' '}
              {formatGermanDateFromTimestamp(serie.naechsteFaelligkeit)}
            </div>
          </div>
          <span
            className={`shrink-0 rounded-md border px-2 py-1 text-xs font-medium ${
              serie.aktiv
                ? 'border-emerald-200 bg-emerald-100 text-emerald-800'
                : 'border-slate-200 bg-slate-100 text-slate-700'
            }`}
          >
            {serie.aktiv ? 'Aktiv' : 'Pausiert'}
          </span>
        </div>
      </Link>
    </li>
  );
}
